import type {ComponentProps, ReactNode} from "react";
import {useContext, useRef, useState} from "react";
import {useTransition, animated, useSpring} from "react-spring";
import UiHandlerContext from "../DSForm/UiHandlerCtx";
import {isWithMetaKey} from "../../utils/device-utils";
import {Box, css} from "../Box/Box";
import {DSSpinner} from "../DSIcon/DSSpinner";
import {DSIcon} from "../DSIcon/DSIcon";

type ButtonState = "initial" | "loading" | "success" | "error";

type PendingOpts<T extends any> = {
  onClick?: null | ((val: T) => unknown);
  onMetaClick?: ComponentProps<"button">["onClick"];
  skipSuccessIcon?: boolean;
  passedState?: ButtonState;
  noErrorHandling?: boolean;
};

const FadeIcon = ({icon, shown}: {shown: boolean; icon: ReactNode}) => {
  const from = {opacity: 0, scale: 0.25, y: "-20%"};
  const enter = {opacity: 1, scale: 1, y: "0%"};
  const renderFn = useTransition(shown, {
    from: from,
    enter: enter,
    leave: from,
    config: {tension: 600, friction: 44},
  });
  return renderFn((iconStyles, item) =>
    item ? (
      <animated.div
        className={css({
          display: "flex",
          flexDir: "column",
          align: "center",
          justify: "center",
          position: "absolute",
          inset: "0",
        })}
        style={iconStyles}
      >
        {icon}
      </animated.div>
    ) : null
  );
};

export const usePendingClick = <T extends any>(opts: PendingOpts<T>) => {
  const [innerState, setIsPending] = useState<ButtonState>("initial");
  const state = opts.passedState || innerState;
  const currClickRef = useRef({});
  const {onButtonError} = useContext(UiHandlerContext);
  const handleClick = (e: T) => {
    const clickFn =
      e && opts.onMetaClick && isWithMetaKey(e as any) ? opts.onMetaClick : opts.onClick;
    if (!clickFn) return;
    const res = clickFn(e as any) as any as Promise<unknown>;
    if (typeof res?.then === "function") {
      const currClickObj = (currClickRef.current = {});
      setIsPending("loading");
      const setter = (val: ButtonState) => {
        if (currClickRef.current === currClickObj) setIsPending(val);
      };
      res.then(
        () => {
          if (opts.skipSuccessIcon) {
            setter("initial");
          } else {
            setter("success");
            setTimeout(() => {
              setter("initial");
            }, 350);
          }
        },
        (error: any) => {
          setter("error");
          setTimeout(() => {
            setter("initial");
          }, 2000);
          if (!opts.noErrorHandling) onButtonError(error);
        }
      );
    }
  };
  return {
    handleClick,
    state,
    icons: (
      <>
        <FadeIcon shown={state === "loading"} icon={<DSSpinner />} />
        <FadeIcon shown={state === "success"} icon={<DSIcon name="Check" label="Success" />} />
        <FadeIcon shown={state === "error"} icon={<DSIcon name="Close" label="Error" />} />
      </>
    ),
  };
};

export const useReactiveIcon = <T extends any>({
  onClick,
  onMetaClick,
  iconEl,
  passedState,
  skipSuccessIcon,
  noErrorHandling,
}: {
  iconEl: ReactNode;
  onClick?: null | ((val: T) => unknown);
  onMetaClick?: ComponentProps<"button">["onClick"];
  passedState?: ButtonState;
  skipSuccessIcon?: boolean;
  noErrorHandling?: boolean;
}) => {
  const {handleClick, icons, state} = usePendingClick({
    onClick,
    onMetaClick,
    passedState,
    skipSuccessIcon,
    noErrorHandling,
  });
  const iconStyles = useSpring({
    y: state === "initial" ? "0%" : "50%",
    scale: state === "initial" ? 1 : 0.25,
    opacity: state === "initial" ? 1 : 0,
    config: {tension: 600, friction: 44},
  });
  const reactiveIcon = (
    <Box relative>
      {icons}
      <animated.div style={iconStyles}>{iconEl}</animated.div>
    </Box>
  );
  return {reactiveIcon, handleClick, state};
};
